import type { ReactNode } from 'react';
import { Dialog as RadixDialog } from 'radix-ui';
import { X } from 'lucide-react';

export interface DrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /**
   * Status chip rendered beside `title`, e.g. a PRIVATE badge.
   *
   * A sibling of the title, never inside it: `title` is bound to
   * `RadixDialog.Title` and is the panel's accessible name, so folding a badge
   * in would append its text to that name for every screen-reader user.
   */
  titleBadge?: ReactNode;
  /** Optional sub-heading. Also wired to aria-describedby. */
  description?: string;
  footer?: ReactNode;
  /**
   * Widen the panel past the standard 420px. For surfaces that switch between
   * reading and editing: a form of dropdowns, uploads and repeatable rows does
   * not fit the reading width, and the alternative - moving the edit to its own
   * page - loses the record the admin is correcting from view.
   */
  wide?: boolean;
  /**
   * Explicit width, overriding `wide`: `form` (600px) is the one width every
   * profile section panel uses, so short and long sections open at the same
   * size and their fields are arranged to fit it (MYN-217).
   */
  size?: 'standard' | 'form' | 'wide';
  children: ReactNode;
}

const WIDTH_CLASS = { standard: 'w-[420px]', form: 'w-[600px]', wide: 'w-[720px]' } as const;

/**
 * Right-anchored slide-in panel (ui-guidelines.md section 11: 420px wide,
 * border-left, themed by the active `data-theme`).
 *
 * Built on Radix Dialog rather than a bespoke element, so focus trapping, Escape
 * handling, scroll locking, and aria wiring come for free and stay consistent
 * with the existing Dialog primitive. This is why the drawer needs no new
 * dependency.
 */
// Sits on --surface-overlay, one step above --surface-raised, so the panel
// reads as a layer ABOVE the page rather than a continuation of it. The
// previous dark treatment used the same colour as the page background, which
// is what made the drawer look welded to the table behind it.
const PANEL_CLASS =
  'bg-surface-overlay border-l border-border-strong rounded-l-card text-text-primary shadow-overlay';

const TITLE_CLASS = 'text-text-primary';
const DESCRIPTION_CLASS = 'text-text-secondary';
const FOOTER_CLASS = 'border-t border-border-subtle';

export function Drawer({
  open,
  onOpenChange,
  title,
  titleBadge,
  description,
  footer,
  wide = false,
  size,
  children,
}: DrawerProps) {
  const widthClass = WIDTH_CLASS[size ?? (wide ? 'wide' : 'standard')];
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-30 bg-scrim backdrop-blur-[2px] data-[state=open]:animate-scrim-in data-[state=closed]:animate-scrim-out" />
        <RadixDialog.Content
          className={`fixed top-0 right-0 z-40 flex h-full ${widthClass} max-w-full flex-col ${PANEL_CLASS} data-[state=open]:animate-drawer-in data-[state=closed]:animate-drawer-out`}
        >
          <header className="flex items-start justify-between gap-4 px-8 py-6">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <RadixDialog.Title className={`text-h5 font-sans ${TITLE_CLASS}`}>
                  {title}
                </RadixDialog.Title>
                {titleBadge}
              </div>
              {description ? (
                <RadixDialog.Description className={`text-p6 mt-1 ${DESCRIPTION_CLASS}`}>
                  {description}
                </RadixDialog.Description>
              ) : null}
            </div>
            <RadixDialog.Close
              aria-label="Close panel"
              className="focus-visible:ring-accent cursor-pointer rounded-lg p-1.5 opacity-70 transition-opacity hover:opacity-100 focus-visible:ring-2 focus-visible:outline-none"
            >
              <X size={20} aria-hidden />
            </RadixDialog.Close>
          </header>

          <div className="flex-1 overflow-y-auto px-8 pb-6">{children}</div>

          {footer ? (
            <footer className={`flex gap-3 px-8 py-6 ${FOOTER_CLASS}`}>{footer}</footer>
          ) : null}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
